import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { FctIcono } from './marca';
import type { FctNombreIcono } from '../iconos';

/* ---------------------------------------------------------------------------
 * FctBoton — para apps sin Ant Design. Con Ant Design usar <Button> + tema.
 * Un solo primario (lima) por pantalla. Verbo + objeto en la etiqueta.
 * ------------------------------------------------------------------------- */
export type FctVarianteBoton = 'primario' | 'secundario' | 'terciario' | 'enlace' | 'destructivo';

export interface FctBotonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variante?: FctVarianteBoton;
  pequeno?: boolean;
  icono?: FctNombreIcono;
}

export function FctBoton({ variante = 'terciario', pequeno = false, icono, className, children, type = 'button', ...resto }: FctBotonProps) {
  const clases = ['fct-boton', `fct-boton--${variante}`, pequeno ? 'fct-boton--sm' : '', className].filter(Boolean).join(' ');
  return (
    <button type={type} className={clases} {...resto}>
      {icono && <FctIcono nombre={icono} />}
      {children}
    </button>
  );
}

/* ---------------------------------------------------------------------------
 * FctSegmentado — vistas o filtros excluyentes con contador (siempre visible, también 0).
 * ------------------------------------------------------------------------- */
export interface FctOpcionSegmentado<T extends string> {
  valor: T;
  etiqueta: string;
  contador?: number;
}

export interface FctSegmentadoProps<T extends string> {
  opciones: FctOpcionSegmentado<T>[];
  valor: T;
  onCambio: (valor: T) => void;
  etiquetaAccesible: string;
}

export function FctSegmentado<T extends string>({ opciones, valor, onCambio, etiquetaAccesible }: FctSegmentadoProps<T>) {
  return (
    <div className="fct-segmentado" role="group" aria-label={etiquetaAccesible}>
      {opciones.map((o) => (
        <button key={o.valor} type="button" aria-pressed={o.valor === valor} onClick={() => onCambio(o.valor)}>
          {o.etiqueta}
          {o.contador !== undefined && <span className="fct-contador">{o.contador.toLocaleString('es-ES')}</span>}
        </button>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * FctPestanas — un solo nivel por pantalla. Con Ant Design usar <Tabs>.
 * ------------------------------------------------------------------------- */
export interface FctPestanasProps<T extends string> {
  pestanas: { valor: T; etiqueta: string }[];
  activa: T;
  onCambio: (valor: T) => void;
  etiquetaAccesible: string;
}

export function FctPestanas<T extends string>({ pestanas, activa, onCambio, etiquetaAccesible }: FctPestanasProps<T>) {
  return (
    <div className="fct-pestanas" role="tablist" aria-label={etiquetaAccesible}>
      {pestanas.map((p) => (
        <button key={p.valor} type="button" role="tab" aria-selected={p.valor === activa} onClick={() => onCambio(p.valor)}>
          {p.etiqueta}
        </button>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * FctFiltroAplicado — filtro activo como etiqueta que se quita con ✕.
 * ------------------------------------------------------------------------- */
export interface FctFiltroAplicadoProps {
  children: ReactNode;
  onQuitar: () => void;
}

export function FctFiltroAplicado({ children, onQuitar }: FctFiltroAplicadoProps) {
  return (
    <span className="fct-etiqueta fct-etiqueta--contorno">
      {children}
      <button type="button" onClick={onQuitar} aria-label="Quitar filtro">
        ✕
      </button>
    </span>
  );
}
